import { useMemo } from 'react';
import OddsCell from '../odds/OddsCell';
import Tip from '../ui/BettingTooltip';
import { formatPrice, formatSpread, formatTotal } from '../../utils/oddsFormat';
import { getBookmakerColumnsFromEvent } from '../../utils/bookmakers';

const MARKETS = [
  { key: 'spreads', label: 'Spread', tip: 'spread' },
  { key: 'h2h', label: 'Moneyline', tip: 'moneyline' },
  { key: 'totals', label: 'Total', tip: 'overunder' },
];

export default function FullOddsComparison({ event }) {
  const columns = useMemo(() => getBookmakerColumnsFromEvent(event), [event]);
  const availableMarkets = MARKETS.filter((mkt) =>
    (event.bookmakers || []).some((bm) => bm.markets?.some((m) => m.key === mkt.key)),
  );

  return (
    <div className="space-y-2">
      <SectionHeader>Odds Comparison</SectionHeader>
      {availableMarkets.map((mkt) => (
        <MarketSection key={mkt.key} event={event} columns={columns} marketKey={mkt.key} label={mkt.label} tip={mkt.tip} />
      ))}
    </div>
  );
}

function SectionHeader({ children }) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-[3px] h-4 bg-[#005BB3] rounded-full" />
      <h3 className="text-[12px] uppercase tracking-[1px] font-medium text-[#005BB3] dark:text-[#3b82f6]" style={{ fontFamily: 'var(--font-sans)' }}>{children}</h3>
    </div>
  );
}

function MarketSection({ event, columns, marketKey, label, tip }) {
  const bookmakerOdds = {};
  for (const bm of event.bookmakers || []) {
    const mkt = bm.markets?.find((m) => m.key === marketKey);
    if (mkt) {
      bookmakerOdds[bm.key] = {
        homeOutcome: mkt.outcomes.find((o) => o.name === event.home_team),
        awayOutcome: mkt.outcomes.find((o) => o.name === event.away_team),
        overOutcome: mkt.outcomes.find((o) => o.name === 'Over'),
        underOutcome: mkt.outcomes.find((o) => o.name === 'Under'),
      };
    }
  }
  const best = findBestOdds(bookmakerOdds, marketKey);

  return (
    <div className="rounded border border-[#E5E7EB] dark:border-[#1e2536] overflow-x-auto">
      <div className="px-3 py-1.5 bg-[#F8F9FA] dark:bg-[#0a0d14] border-b border-[#E5E7EB] dark:border-[#1e2536]">
        <span className="text-[14px] font-semibold text-[#111827] dark:text-[#e2e8f0]" style={{ fontFamily: 'var(--font-sans)' }}><Tip term={tip}>{label}</Tip></span>
      </div>
      <table className="w-full" style={{ fontFamily: 'var(--font-mono)' }}>
        <thead>
          <tr className="text-[#6B7280] dark:text-[#505a6e]">
            <th className="text-left px-3 py-1.5 text-[12px] uppercase tracking-[0.5px] font-semibold min-w-[110px]" style={{ fontFamily: 'var(--font-sans)' }}>Team</th>
            {columns.map((col) => (
              <th key={col.key} className="text-center px-2 py-1.5 text-[12px] uppercase tracking-[0.5px] font-semibold min-w-[100px] border-l border-[#E5E7EB] dark:border-[#1e2536]" style={{ fontFamily: 'var(--font-sans)' }}>{col.title}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {marketKey === 'totals' ? (
            <>
              <OddsRow label="Over" columns={columns} bookmakerOdds={bookmakerOdds} side="overOutcome" marketKey={marketKey} bestKey="overBest" worstKey="overWorst" best={best} />
              <OddsRow label="Under" columns={columns} bookmakerOdds={bookmakerOdds} side="underOutcome" marketKey={marketKey} bestKey="underBest" worstKey="underWorst" best={best} />
            </>
          ) : (
            <>
              <OddsRow label={event.away_team} columns={columns} bookmakerOdds={bookmakerOdds} side="awayOutcome" marketKey={marketKey} bestKey="awayBest" worstKey="awayWorst" best={best} />
              <OddsRow label={event.home_team} columns={columns} bookmakerOdds={bookmakerOdds} side="homeOutcome" marketKey={marketKey} bestKey="homeBest" worstKey="homeWorst" best={best} />
            </>
          )}
        </tbody>
      </table>
    </div>
  );
}

function OddsRow({ label, columns, bookmakerOdds, side, marketKey, bestKey, worstKey, best }) {
  const totalLabel = label === 'Over' ? 'O' : 'U';
  return (
    <tr className="border-t border-[#E5E7EB] dark:border-[#1e2536]">
      <td className="px-3 py-1.5 text-[14px] font-medium text-[#111827] dark:text-[#e2e8f0]" style={{ fontFamily: 'var(--font-sans)' }}>{label}</td>
      {columns.map((col) => {
        const data = bookmakerOdds[col.key];
        const outcome = data?.[side];
        if (!outcome) return <td key={col.key} className="text-center px-2 py-1.5 text-[#D1D5DB] dark:text-[#2a3348] border-l border-[#E5E7EB] dark:border-[#1e2536]">—</td>;
        const highlight = best[bestKey] === col.key ? 'best' : best[worstKey] === col.key ? 'worst' : null;
        let text;
        if (marketKey === 'spreads') text = formatSpread(outcome.point, outcome.price);
        else if (marketKey === 'totals') text = formatTotal(totalLabel, outcome.point, outcome.price);
        else text = formatPrice(outcome.price);
        return <td key={col.key} className="text-center px-2 py-1.5 border-l border-[#E5E7EB] dark:border-[#1e2536]"><OddsCell text={text} highlight={highlight} /></td>;
      })}
    </tr>
  );
}

function findBestOdds(bookmakerOdds, market) {
  const result = {};
  const sides = market === 'totals'
    ? [['overOutcome', 'overBest', 'overWorst'], ['underOutcome', 'underBest', 'underWorst']]
    : [['homeOutcome', 'homeBest', 'homeWorst'], ['awayOutcome', 'awayBest', 'awayWorst']];
  for (const [sideKey, bestKey, worstKey] of sides) {
    let best = { bk: null, price: -Infinity };
    let worst = { bk: null, price: Infinity };
    for (const [bk, data] of Object.entries(bookmakerOdds)) {
      const o = data[sideKey];
      if (o) { if (o.price > best.price) best = { bk, price: o.price }; if (o.price < worst.price) worst = { bk, price: o.price }; }
    }
    result[bestKey] = best.bk;
    result[worstKey] = worst.bk;
  }
  return result;
}
